import React, { useState, useCallback } from 'react';
import { MessageSquarePlus, Plus, Trash2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'ivoliq_sb_text_templates';

// Standard-Rückfragen, die ein Steuerberater häufig an Mandanten stellt.
const DEFAULT_TEMPLATES = [
  { id: 'd1', text: 'Bitte reiche den fehlenden Beleg zu dieser Buchung nach.' },
  { id: 'd2', text: 'Bei diesem Beleg fehlt der ausgewiesene Umsatzsteuersatz — bitte ergänzen.' },
  { id: 'd3', text: 'Bitte prüfe, ob hier Reverse-Charge (§ 13b UStG) anzuwenden ist.' },
  { id: 'd4', text: 'Bei dieser Bewirtung fehlen die Pflichtangaben (Anlass, Teilnehmer). Bitte nachtragen.' },
  { id: 'd5', text: 'Diese Ausgabe scheint privat veranlasst — bitte bestätige die betriebliche Nutzung.' },
  { id: 'd6', text: 'Bitte ordne dieser Buchung die korrekte Kategorie zu.' },
  { id: 'd7', text: 'Der Zahlungseingang zu dieser Rechnung fehlt — wurde sie bereits beglichen?' },
  { id: 'd8', text: 'Vielen Dank, der Vorgang ist damit geklärt.' },
];

function loadCustom() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCustom(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* */ }
}

/**
 * Textbaustein-Vorlagen — Popover mit durchsuchbarer Liste.
 * `onSelect(text)` hängt den Text an das aktuelle Eingabefeld an.
 * `currentValue` (optional) ermöglicht "Aktuellen Text als Vorlage speichern".
 */
export default function TextTemplatePopover({ onSelect, currentValue = '', size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(loadCustom);

  const handleSelect = useCallback((text) => {
    onSelect?.(text);
    setOpen(false);
  }, [onSelect]);

  const handleSaveNew = useCallback(() => {
    const val = (currentValue || '').trim();
    if (!val) return;
    const next = [...custom, { id: `c${Date.now()}`, text: val }];
    setCustom(next);
    saveCustom(next);
  }, [currentValue, custom]);

  const handleDelete = useCallback((id, e) => {
    e.stopPropagation();
    const next = custom.filter((t) => t.id !== id);
    setCustom(next);
    saveCustom(next);
  }, [custom]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={size}
          className="flex items-center gap-2 rounded-lg bg-slate-50 text-slate-600"
          data-testid="template-dropdown-trigger"
        >
          <MessageSquarePlus className="h-4 w-4" />Vorlage
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Vorlage suchen…" />
          <CommandList>
            <CommandEmpty>Keine Vorlage gefunden.</CommandEmpty>
            <CommandGroup heading="Standard-Vorlagen">
              {DEFAULT_TEMPLATES.map((t) => (
                <CommandItem
                  key={t.id}
                  value={t.text}
                  onSelect={() => handleSelect(t.text)}
                  className="cursor-pointer text-sm"
                  data-testid={`template-item-${t.id}`}
                >
                  {t.text}
                </CommandItem>
              ))}
            </CommandGroup>
            {custom.length > 0 && (
              <CommandGroup heading="Eigene Vorlagen">
                {custom.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.text}
                    onSelect={() => handleSelect(t.text)}
                    className="group cursor-pointer text-sm"
                    data-testid={`template-item-${t.id}`}
                  >
                    <span className="flex-1">{t.text}</span>
                    <button
                      onClick={(e) => handleDelete(t.id, e)}
                      className="ml-2 opacity-0 transition-opacity group-hover:opacity-100"
                      title="Vorlage löschen"
                      data-testid={`template-delete-${t.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-500" />
                    </button>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {(currentValue || '').trim() && (
            <div className="border-t border-slate-100 p-2">
              <button
                onClick={handleSaveNew}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
                data-testid="template-save-new"
              >
                <Plus className="h-4 w-4" />Aktuellen Text als Vorlage speichern
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
